import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import ErrorMessage from '@components/Common/ErrorMessage';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import SecurityIcon from '@mui/icons-material/Security';
import { Box, Chip, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UserCreateEditPopup from './UserCreateEditPopup';
import UserWorkspace from './UserWorkspace';
import './index.scss';
import { formatRole, isLegacyRole } from '@components/Common/Utils/roles';
import { formatDate } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const USER_COLUMNS = ['fullName', 'email', 'role', 'isActive', 'createdAt'];

export type UserRole = 'SUPER_ADMIN' | 'HR_LEAD' | 'HR' | 'MARKETING';

export interface AdminUser {
    _id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    /** The stored value: may be a legacy one ("admin", "VIEWER") or absent. */
    role: UserRole | string | null;
    isActive: boolean;
    createdAt: string;
}

const rowsPerPage = 100;

const ROLE_CHIP_SX: Record<string, { backgroundColor: string; color: string }> = {
    SUPER_ADMIN: { backgroundColor: 'var(--color-stage-qualified-bg)', color: 'var(--color-stage-qualified-text)' },
    HR_LEAD: { backgroundColor: 'var(--color-stage-won-bg)', color: 'var(--color-stage-won-text)' },
    HR: { backgroundColor: 'var(--color-stage-lead-bg)', color: 'var(--color-stage-lead-text)' },
    MARKETING: { backgroundColor: 'var(--color-stage-proposal-bg)', color: 'var(--color-stage-proposal-text)' },
};
/* Legacy values grant Super Admin and are due for migration: flagged so nobody reads them as a
   lesser role. A missing or unrecognised role grants nothing, and is shown as such rather than
   dressed up as HR. */
const LEGACY_CHIP_SX = { backgroundColor: 'var(--color-stage-negotiation-bg)', color: 'var(--color-stage-negotiation-text)' };
const UNKNOWN_CHIP_SX = { backgroundColor: 'var(--color-stage-lost-bg)', color: 'var(--color-stage-lost-text)' };

export const roleChipSx = (role?: string | null) =>
    (role && ROLE_CHIP_SX[role]) || (isLegacyRole(role) ? LEGACY_CHIP_SX : UNKNOWN_CHIP_SX);

const UserRBAC = () => {
    const { userId } = useParams<{ userId?: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const isNew = location.pathname.endsWith('/new');
    const isEdit = !!userId && !isNew;

    const [page, setPage] = useState(() => {
        const p = parseInt(searchParams.get('page') ?? '0', 10);
        return Number.isFinite(p) && p >= 0 ? p : 0;
    });
    const [data, setData] = useState<AdminUser[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchUsers = useCallback(async (pageNum: number) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`/api/users?page=${pageNum}&size=${rowsPerPage}`);
            const result = response.data;
            const users: AdminUser[] = (result.content ?? []).map((u: any) => ({
                _id: u._id,
                firstName: u.personalDetails?.firstName ?? '',
                lastName: u.personalDetails?.lastName ?? '',
                fullName: u.personalDetails?.fullName || `${u.personalDetails?.firstName ?? ''} ${u.personalDetails?.lastName ?? ''}`.trim(),
                email: u.personalDetails?.email ?? '',
                // No default: a missing role is not HR (login mints "admin" for it, which is Super Admin).
                role: u.role ?? null,
                isActive: u.isActive !== false,
                createdAt: u.createdAt ?? '',
            }));
            setData(users);
            setTotalElements(result.totalElements ?? users.length);
        } catch {
            setError('Failed to fetch users. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isNew && !isEdit) fetchUsers(page);
    }, [fetchUsers, page, isNew, isEdit]);

    useEffect(() => {
        const p = parseInt(searchParams.get('page') ?? '0', 10);
        const safe = Number.isFinite(p) && p >= 0 ? p : 0;
        if (safe !== page) setPage(safe);
    }, [searchParams.toString()]);

    const handleChangePage = (_event: React.MouseEvent<Element, MouseEvent> | null, newPage: number) => {
        setPage(newPage);
        const newSearch = new URLSearchParams(searchParams);
        newSearch.set('page', String(newPage));
        if (newSearch.toString() !== searchParams.toString()) setSearchParams(newSearch);
    };

    // The workspace loads its own record, so there is no state to hand over —
    // and no stale copy when someone lands on the URL directly.
    const handleRowClick = (row: any) => navigate(`/user-rbac/${row.id}`);

    const handleSaveSuccess = () => {
        navigate('/user-rbac');
    };

    // One record opens in its entity workspace.
    if (isEdit) return <UserWorkspace />;

    // Create view
    if (isNew) {
        return (
            <UserCreateEditPopup
                mode="create"
                onSaveSuccess={handleSaveSuccess}
            />
        );
    }

    return (
        <CRMPageTemplate
            addNewLabel="Add User"
            icon={<SecurityIcon />}
            title="Users"
            totalCount={totalElements}
            onAddNew={() => navigate('/user-rbac/new')}
        >
            {loading ? (
                <SkeletonLoader rows={20} columns={USER_COLUMNS.length} />
            ) : error ? (
                <ErrorMessage message={error} />
            ) : data.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
                    <Typography color="text.secondary" variant="body2">No users found.</Typography>
                </Box>
            ) : (
                <MintlyTable
                    pagination={{
                        enabled: true,
                        page,
                        rowsPerPage,
                        totalNumberOfRows: totalElements,
                        handleChangePage,
                    }}
                    rows={data.map(u => ({ id: u._id, ...u }))}
                    selectors={USER_COLUMNS}
                    tableHeadRow={{
                        fullName: <>Name</>,
                        email: <>Email</>,
                        role: <>Role</>,
                        isActive: <>Status</>,
                        createdAt: <>Created</>,
                    }}
                    tableBodyRow={{
                        fullName: (row: any) => <>{row.fullName || `${row.firstName} ${row.lastName}`}</>,
                        email: (row: any) => <>{row.email}</>,
                        role: (row: any) => {
                            return (
                                <Chip
                                    label={formatRole(row.role)}
                                    size="small"
                                    sx={roleChipSx(row.role)}
                                />
                            );
                        },
                        isActive: (row: any) => (
                            <CRMStatusChip status={row.isActive ? 'active' : 'inactive'} />
                        ),
                        createdAt: (row: any) => (
                            <>{formatDate(row.createdAt)}</>
                        ),
                    }}
                    onRowClick={handleRowClick}
                />
            )}
        </CRMPageTemplate>
    );
};

export default UserRBAC;

